/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import cx from 'classnames';

import { Button } from '@coursera/cds-core';

import type { Props as CoachTriggerButtonProps } from 'bundles/ai-coach-platform/components/building-blocks/CoachTriggerButton';
import SparkleIcon from 'bundles/unified-common/components/coach-on-discovery/SparkleIcon';

export type Props = CoachTriggerButtonProps & {
  /** If true, animation will play automatically when component mounts */
  showAnimationOnMount?: boolean;
};

const styles = {
  coachButton: ({ showBorder }: { showBorder: boolean }) => css`
    /* Custom gradients */
    --default-gradient: radial-gradient(436.45% 146.99% at 19.79% -48.61%, #1cffba 0%, rgb(8 255 181 / 0%) 65.49%),
      linear-gradient(275deg, #0ff0ff -26.6%, #4b95ff 31.52%, #1e72eb 110.8%);

    border-radius: var(--cds-border-radius-max);
    box-shadow: none;

    &:hover,
    &:active,
    &.selected,
    &.cds-button-focusVisible {
      box-shadow: none;
    }

    // override the focus outline on the button.
    &.cds-button-focusVisible.cds-button-disableElevation::after {
      border-radius: var(--cds-border-radius-max);
      box-shadow: 0 0 0 1px var(--cds-color-emphasis-primary-stroke-default),
        0 0 0 2px var(--cds-color-interactive-background-primary-hover-weak);
    }

    &.coach-secondary-button {
      position: relative;
      z-index: 1;
      background: ${showBorder ? 'var(--cds-color-neutral-stroke-primary-weak)' : 'transparent'};
      transition: background 0.5s ease-in;

      .cds-button-label {
        color: var(--cds-color-neutral-primary);
      }

      &::before {
        content: '';
        position: absolute;
        left: 1px;
        right: 1px;
        top: 1px;
        bottom: 1px;
        border-radius: var(--cds-border-radius-max);
        background: var(--cds-color-neutral-background-primary);
        z-index: -1;
      }

      &:hover,
      &:active,
      &.cds-button-focusVisible,
      &.selected {
        &::before {
          background: var(--cds-color-interactive-background-primary-hover-weak);
        }
      }

      &:active {
        &::before {
          border-radius: var(--cds-border-radius-max);
        }

        background: var(--default-gradient);
      }

      &.selected {
        background: var(--default-gradient);

        &::before {
          left: 2px;
          right: 2px;
          top: 2px;
          bottom: 2px;
        }
      }

      &[aria-disabled='true'] {
        .animation-wrapper {
          &::after {
            background: unset;
          }
        }

        .cds-button-label {
          color: var(--cds-color-neutral-disabled);
        }

        svg path {
          fill: var(--cds-color-neutral-disabled);
        }
      }
    }
  `,
  animated: ({ iterationCount }: { iterationCount: number | 'infinite' }) => css`
    --coursera-ai-color-teal: #0ff0ff;
    @property --angle {
      syntax: '<angle>';
      inherits: false;
      initial-value: 0deg;
    }

    @keyframes spin {
      from {
        --angle: 0deg;
      }

      to {
        --angle: 360deg;
      }
    }

    /* webpack changes this to 80% for rtl if this is inline, so we create a property to avoid this */
    --transparent-percentage: 20%;

    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: var(--cds-border-radius-max);

    &::after {
      content: '';
      position: absolute;
      height: 100%;
      width: 100%;
      background: conic-gradient(
        from var(--angle),
        transparent var(--transparent-percentage),
        var(--coursera-ai-color-teal),
        var(--cds-color-emphasis-primary-background-default),
        var(--cds-color-emphasis-primary-background-default),
        var(--coursera-ai-color-teal)
      );
      z-index: -2;
      border-radius: var(--cds-border-radius-max);
      animation: spin 3s linear forwards ${iterationCount};

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }

    &::before {
      content: '';
      position: absolute;
      top: 1px;
      left: 1px;
      right: 1px;
      bottom: 1px;
      background: var(--cds-color-interactive-background-primary-hover-weak);
      z-index: -1;
      border-radius: var(--cds-border-radius-max);
    }
  `,
};

const StyledCoachTriggerButton = React.forwardRef(
  (props: Props, ref: React.Ref<HTMLButtonElement>): React.ReactElement => {
    const {
      variant = 'secondary',
      size = 'small',
      className,
      selected,
      children,
      showAnimationOnMount = false,
      ...rest
    } = props;
    const [showAnimation, setShowAnimation] = useState(showAnimationOnMount);
    const animationRef = useRef<HTMLSpanElement>(null);
    const animationIterationCount = useRef<number | 'infinite'>(2);

    useEffect(() => {
      const currentAnimationRef = animationRef.current;

      const handleAnimationEnd = () => {
        if (animationIterationCount.current === 2) {
          animationIterationCount.current = 'infinite';
        }
        setShowAnimation(false);
      };

      if (currentAnimationRef) {
        currentAnimationRef.addEventListener('animationend', handleAnimationEnd);
      }

      return () => {
        if (currentAnimationRef) {
          currentAnimationRef.removeEventListener('animationend', handleAnimationEnd);
        }
      };
    }, []);

    return (
      <Button
        ref={ref}
        variant={variant}
        textAlign="start"
        size={size}
        icon={<SparkleIcon size={size} />}
        iconPosition="before"
        css={styles.coachButton({ showBorder: !showAnimation })}
        className={cx(className, { selected }, variant === 'secondary' ? 'coach-secondary-button' : '')}
        onMouseEnter={() => {
          setShowAnimation(true);
        }}
        onMouseLeave={() => {
          setShowAnimation(false);
        }}
        {...rest}
      >
        <>
          {children}
          <span
            className="animation-wrapper"
            css={showAnimation && styles.animated({ iterationCount: animationIterationCount.current })}
            ref={animationRef}
          />
        </>
      </Button>
    );
  }
) as React.ComponentType<Props>;

export default StyledCoachTriggerButton;
